import { effectiveRir } from "./effortScale.js";
import type { LogSet } from "./schemas/logSet.js";

// THE funnel for the second strength axis (measurement-contract §8) —
// "reps at a fixed load, over time". The sibling of e1rmPoints.ts: same
// shape (window param, one Map<slug,…>, sorted-once, a display `snapshot`
// name), a different QUESTION.
//
// e1RM asks "how heavy a single rep could you do" and needs a heavy, low-rep
// series near the limit — which for high-rep and machine work never exists
// (Epley refuses to extrapolate past ~12 reps by design; e1rmConfidence.ts).
// This axis asks the transposed question: "at the load W you keep coming back
// to, are the reps climbing at equal-or-better effort?" — a directly OBSERVED
// gain, no extrapolation, covering the ~46% of training the e1RM axis is blind
// to (measured 2026-07-24 on 15.5k of Antenore's sets: leg-extension,
// triceps-pushdown, cable work — 0-4 e1RM points, but rich repeated-load data).
//
// ⛔ Not a PRsByExercise duplicate (pr.ts): that keys reps→best-ever-weight, a
// single all-time record per rep bucket with no chronology. This keys
// load-bucket→reps-over-TIME. The transpose, genuinely new.

/**
 * One dated observation of "N reps at a fixed load".
 *
 * `rir` is the set's EFFECTIVE RIR (effortScale.ts — folds fail→0, falls back
 * to rpe). `null` when the set carried no effort tag at all. ⛔ Null is NOT
 * zero and NOT "easy" — it is a set we know nothing about, and a reps count
 * without a known effort is not comparable (20 easy reps and 20 hard reps at
 * the same load are different data). Consumers decide what to do with null
 * explicitly, exactly as in e1rmPoints.ts.
 */
export type RepsAtLoadPoint = {
  atMs: number;
  reps: number;
  rir: number | null;
};

/** One exercise's reps-at-load series, at its single most-informative load. */
export type RepsAtLoadSeries = {
  /**
   * `exerciseName` as stamped on the FIRST set seen for this slug. Display
   * FALLBACK for a deleted catalog row, never the render source — the live
   * name is resolved through the slug (the foreign key), like e1rmPoints.
   */
  snapshot: string | undefined;
  /**
   * The representative fixed load this series is measured at, in kg. The MODE
   * of the bucket's set weights (the number the athlete actually re-loads to),
   * so the readout can name it — "18 reps at 55 kg", not a bucket centroid.
   */
  loadKg: number;
  /** Chronological, oldest first. Every set in the representative load bucket
   *  (effort-tagged and not) — the consumer filters to the effort-tagged ones
   *  for the trend, but keeps the rest to know the load recurs. */
  points: RepsAtLoadPoint[];
  /**
   * How many DISTINCT days carry an effort-tagged set at this load. The gate
   * for measurement-contract §3's "measured" vs "insufficient": a paired
   * "then→now" comparison needs ≥2 effort-controlled days at the same load.
   */
  comparableDays: number;
};

/** The window to keep, in epoch ms. Both bounds inclusive; omit for no bound. */
export type RepsAtLoadWindow = { fromMs?: number; toMs?: number };

/**
 * The "same load" tolerance for ONE exercise, DERIVED from that exercise's own
 * loads — never a hard-coded constant (Antenore 2026-07-24: a constant baked
 * from one athlete breaks on a powerlifter or on tomorrow's training; the RULE
 * is universal, the number it yields is personal and lives at read time).
 *
 * = a quarter of the athlete's own typical increment on this exercise (the
 * MEDIAN gap between the distinct loads they've used). Two sets share a load
 * only when closer than that. Proven on real data (2026-07-24): it keeps every
 * barbell step distinct (a 2.5 kg grid → tol ~0.6 kg, nothing merges) while
 * absorbing the sub-kg float/pin dust on machine stacks (58.5 vs 58.7) — the
 * paired population lands at the exact-match number, so the tolerance buys
 * robustness, not signal. A blunt ±2.5 kg instead would fuse 100↔102.5 kg squat
 * into "the same load" and erase the very progression this axis measures.
 */
export const bucketToleranceKg = (distinctSortedWeights: number[]): number => {
  if (distinctSortedWeights.length < 2) return 0;
  const gaps: number[] = [];
  for (let i = 1; i < distinctSortedWeights.length; i++) {
    const g = distinctSortedWeights[i]! - distinctSortedWeights[i - 1]!;
    if (g > 1e-9) gaps.push(g);
  }
  if (gaps.length === 0) return 0;
  gaps.sort((a, b) => a - b);
  const mid = Math.floor(gaps.length / 2);
  const median =
    gaps.length % 2 === 1 ? gaps[mid]! : (gaps[mid - 1]! + gaps[mid]!) / 2;
  return median * 0.25;
};

// Single-linkage clustering of an exercise's weights into load buckets, using
// the per-exercise tolerance above. Returns weight → bucket key (the bucket's
// mode weight, i.e. the load the athlete used most often in that bucket — the
// number worth showing).
const bucketWeights = (weights: number[]): Map<number, number> => {
  const distinct = Array.from(new Set(weights)).sort((a, b) => a - b);
  const tol = bucketToleranceKg(distinct);
  const freq = new Map<number, number>();
  for (const w of weights) freq.set(w, (freq.get(w) ?? 0) + 1);

  const map = new Map<number, number>();
  let cluster: number[] = [];
  const flush = () => {
    if (cluster.length === 0) return;
    // Bucket key = the modal weight (heaviest wins a tie — the harder set is
    // the more honest label for a merged bucket). Not the centroid: a clean
    // "55 kg" the athlete recognises, not "54.7".
    let mode = cluster[0]!;
    let best = -1;
    for (const w of cluster) {
      const f = freq.get(w) ?? 0;
      if (f > best || (f === best && w > mode)) {
        best = f;
        mode = w;
      }
    }
    for (const w of cluster) map.set(w, mode);
    cluster = [];
  };
  for (const w of distinct) {
    if (cluster.length === 0 || w - cluster[cluster.length - 1]! <= tol + 1e-9) {
      cluster.push(w);
    } else {
      flush();
      cluster.push(w);
    }
  }
  flush();
  return map;
};

const dayOf = (iso: string): string => iso.slice(0, 10);

/**
 * Build `slug → series` from raw LogSets.
 *
 * Reads RAW sets (not the session-result cache, which keeps only each
 * session's top set and throws away the repeated-load high-rep sets this axis
 * is about — measurement-contract §8 / fetta3-axis2-plan). For each exercise
 * it buckets the loads by the per-exercise tolerance, finds the loads that
 * RECUR (≥2 distinct days), and keeps the single most-informative one:
 * preferring a load that is measurable (has effort-tagged days), so an exercise
 * with a recurring but un-tagged load still surfaces its honest "insufficient"
 * nudge instead of vanishing.
 */
export const repsAtLoadBySlug = (
  sets: readonly LogSet[],
  window?: RepsAtLoadWindow,
): Map<string, RepsAtLoadSeries> => {
  const fromMs = window?.fromMs ?? Number.NEGATIVE_INFINITY;
  const toMs = window?.toMs ?? Number.POSITIVE_INFINITY;

  type Raw = {
    atMs: number;
    day: string;
    weight: number;
    reps: number;
    rir: number | null;
    name: string | undefined;
  };
  const bySlug = new Map<string, Raw[]>();

  for (const s of sets) {
    // A reps-at-load observation: an externally loaded set with real reps.
    // (bodyweight-only sets have no fixed external load to hold constant.)
    if (s.kind !== "weighted" && s.kind !== "bodyweight") continue;
    if (s.reps == null || s.reps <= 0) continue;
    if (s.weight == null || s.weight <= 0) continue;
    const atMs = Date.parse(s.performedAt);
    if (Number.isNaN(atMs) || atMs < fromMs || atMs > toMs) continue;
    const arr = bySlug.get(s.exerciseSlug) ?? [];
    arr.push({
      atMs,
      day: dayOf(s.performedAt),
      weight: s.weight,
      reps: s.reps,
      rir: effectiveRir(s),
      name: s.exerciseName,
    });
    bySlug.set(s.exerciseSlug, arr);
  }

  const out = new Map<string, RepsAtLoadSeries>();
  for (const [slug, raws] of bySlug) {
    const bucketOf = bucketWeights(raws.map((r) => r.weight));

    // Group this exercise's sets by load bucket.
    type Bucket = { loadKg: number; rows: Raw[] };
    const buckets = new Map<number, Bucket>();
    for (const r of raws) {
      const key = bucketOf.get(r.weight)!;
      const b = buckets.get(key) ?? { loadKg: key, rows: [] };
      b.rows.push(r);
      buckets.set(key, b);
    }

    // Keep only loads that RECUR (≥2 distinct days) — a single day at a load
    // is not a "then→now" comparison. Score each by measurability first
    // (effort-tagged distinct days), then by total distinct days, then load.
    let best: { bucket: Bucket; effortDays: number; days: number } | null = null;
    for (const b of buckets.values()) {
      const days = new Set(b.rows.map((r) => r.day));
      if (days.size < 2) continue;
      const effortDays = new Set(
        b.rows.filter((r) => r.rir != null).map((r) => r.day),
      ).size;
      if (
        best === null ||
        effortDays > best.effortDays ||
        (effortDays === best.effortDays && days.size > best.days) ||
        (effortDays === best.effortDays &&
          days.size === best.days &&
          b.loadKg > best.bucket.loadKg)
      ) {
        best = { bucket: b, effortDays, days: days.size };
      }
    }
    if (best === null) continue; // no recurring load → this axis has nothing

    // ONE point per DAY, not per set (PG-4).
    //
    // Emitting a point per raw row put every set of a session at the same
    // instant on the time axis: three sets at 40 kg on Jul 12 became three
    // points stacked on one x, 6 px apart, under dot markers ~10× taller than
    // that. On screen it read as "curve tutte rotte, punti qui e là, segmenti
    // rotti" (Antenore's smoke) — and it was not only ugly. The chart derives
    // its cadence from the median gap between points, so minutes-apart sets
    // dragged that median down to minutes, and then EVERY real gap between
    // sessions (days) blew past the "this is a hole" threshold and broke the
    // path. One axis, two bugs, one cause: the wrong grain.
    //
    // e1rmPointsByTrack has always aggregated per session — this brings the
    // second axis onto the same grain instead of inventing a rendering
    // workaround downstream.
    //
    // WHICH set represents the day: the one with the MOST reps, because at a
    // fixed load that is the day's best demonstration of capacity. Effort-
    // tagged rows win over untagged ones first, though — `comparableDays`
    // counts effort-tagged days, so letting an untagged row represent a day
    // that HAS a tagged one would make a comparable day look uncomparable.
    const byDay = new Map<string, Raw>();
    for (const r of best.bucket.rows) {
      const cur = byDay.get(r.day);
      if (cur === undefined) {
        byDay.set(r.day, r);
        continue;
      }
      const rTagged = r.rir != null;
      const curTagged = cur.rir != null;
      if (rTagged !== curTagged) {
        if (rTagged) byDay.set(r.day, r);
        continue;
      }
      if (r.reps > cur.reps) byDay.set(r.day, r);
    }
    const points: RepsAtLoadPoint[] = [...byDay.values()]
      .map((r) => ({ atMs: r.atMs, reps: r.reps, rir: r.rir }))
      .sort((a, b) => a.atMs - b.atMs);

    out.set(slug, {
      snapshot: raws[0]!.name,
      loadKg: best.bucket.loadKg,
      points,
      comparableDays: best.effortDays,
    });
  }

  return out;
};
